import { useState } from "react";
import axios from "axios";
import { Lock, Loader2, Inbox } from "lucide-react";
import { LOGO_URL } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Admin() {
    const [passphrase, setPassphrase] = useState("");
    const [rows, setRows] = useState(null);
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const load = async () => {
        if (!passphrase || loading) return;
        setLoading(true);
        setError("");
        try {
            const res = await axios.get(`${API}/admin/enquiries`, {
                headers: { "X-Admin-Passphrase": passphrase },
            });
            setRows(res.data);
        } catch {
            setRows(null);
            setError("Invalid passphrase or couldn't load enquiries.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="min-h-screen bg-[#FAFAFC] text-slate-900" data-testid="admin-page">
            <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14">
                <div className="flex items-center gap-3">
                    <img src={LOGO_URL} alt="Brain Brush logo" className="h-10 w-10 rounded-lg" />
                    <div>
                        <h1 className="font-display font-bold text-xl">Project Enquiries</h1>
                        <p className="text-xs text-slate-500">Brain Brush — owner view</p>
                    </div>
                </div>

                <div className="mt-8 flex max-w-lg gap-2 items-end">
                    <div className="flex-1">
                        <label htmlFor="admin-passphrase" className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                            Admin passphrase
                        </label>
                        <div className="mt-2 relative">
                            <Lock size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                id="admin-passphrase"
                                data-testid="admin-passphrase-input"
                                type="password"
                                value={passphrase}
                                onChange={(e) => setPassphrase(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && load()}
                                placeholder="Enter passphrase"
                                className="w-full rounded-xl border border-slate-200 bg-white pl-10 pr-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500"
                            />
                        </div>
                    </div>
                    <button
                        data-testid="admin-load-button"
                        onClick={load}
                        disabled={loading || !passphrase}
                        className="gradient-bg text-white text-sm font-semibold px-5 py-3 rounded-xl inline-flex items-center gap-2 disabled:opacity-60"
                    >
                        {loading && <Loader2 size={14} className="animate-spin" />}
                        {loading ? "Loading…" : "View enquiries"}
                    </button>
                </div>

                {error && <p data-testid="admin-error" className="mt-4 text-sm text-rose-600">{error}</p>}

                {rows && rows.length === 0 && (
                    <div data-testid="admin-empty" className="mt-10 rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500">
                        <Inbox size={28} className="mx-auto mb-3 text-slate-300" />
                        No enquiries yet — they'll appear here when someone submits the contact form.
                    </div>
                )}

                {rows && rows.length > 0 && (
                    <div className="mt-8 space-y-4" data-testid="admin-enquiry-list">
                        {rows.map((row) => (
                            <div key={row.id} data-testid={`admin-enquiry-${row.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="font-semibold text-slate-900">{row.name}</div>
                                    <div className="text-xs text-slate-400">{new Date(row.created_at).toLocaleString()}</div>
                                </div>
                                <div className="mt-1 text-sm text-violet-700">{row.email}{row.company ? ` · ${row.company}` : ""}</div>
                                <div className="mt-2 flex flex-wrap gap-2">
                                    {row.phone && <span className="text-[11px] font-medium bg-violet-50 text-violet-700 border border-violet-100 rounded-full px-3 py-1">Phone: {row.phone}</span>}
                                    {row.timeline && <span className="text-[11px] font-medium bg-pink-50 text-pink-700 border border-pink-100 rounded-full px-3 py-1">Timeline: {row.timeline}</span>}
                                </div>
                                <p className="mt-3 text-sm text-slate-600 leading-relaxed whitespace-pre-wrap">{row.message}</p>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
